import React from "react";
import ReactDOM from "react-dom";
import { Router } from "react-router-dom";
import { Provider } from "react-redux";
import io from "socket.io-client";

import { SnackbarProvider } from "notistack";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import { HighlightOff } from "@material-ui/icons";

import "./index.scss";
import "./App.scss";

import App from "./App";
import History from "./History";

// Redux store
import store from "./store";
import snackbarStore from "./snackbarStore";

// Configuration
import config from "./config/config";
// For creating Rest calls with Axios
import { createInstance as createRestInstance } from "./modules/np-rest/rest";
import { SnackbarUtilsConfigurator } from "./modules/sc-utilities/SnackBar";

import { UPDATE_USER_STORAGE } from './modules/sc-core/Auth/store/actionTypes';
import { USER_CONNECTED, TOKEN_VALIDATION, GET_TRANSLATIONS, TRANSLATIONS, NAMESPACE } from './redis-constants'
import { SESSION } from "./local-storage-constants";
import CustomSnackbar from "./modules/sc-utilities/snackbar/CustomSnackbar";
import { getBrowserLanguage, updateTranslations } from "./modules/sc-utilities/TranslationsUtilities";

const updateUserStorage = userStorage => {
    return {
        type: UPDATE_USER_STORAGE,
        userStorage: userStorage
    };
};

function handleTokenValidation(message) {
    // If token is not valid we should return to login and clear storage
    if (!message.valid) {
        localStorage.clear();
        window.location = "/login";
        return;
    }

    const userStorage = message?.UserStorage ? message.UserStorage : {};
    const browserLanguage = getBrowserLanguage();
    const defaultLanguage = userStorage?.UserSettings?.DefaultLanguage? userStorage?.UserSettings?.DefaultLanguage : browserLanguage?? "en";
    this.emit(GET_TRANSLATIONS, { LanguageKey: defaultLanguage });
    store.dispatch(updateUserStorage(userStorage));
}

export const socket = io(`${config.backend_domain}:${config.backend_port}/${NAMESPACE}`, { transports: ["websocket"] });
// Wrap everything into an async function to be able to use await
const index = async () => {
    document.title = `${process.env.REACT_APP_SECURAZE_SERVER} - Securaze Dashboard`;
    const snackbarRef = React.createRef();
    const onClickDismiss = key => () => {
        snackbarRef.current.handleDismissSnack(key);
    }
    // Create Axios instance for REST calls
    // Note: it is possible to create multiple Rest instances by providing the name property.
    // In our case the name property is not provided, so the default '_DEFAULT_' will be used
    // In this way the components can use the axios instance with const rest = getAxiosInstance();
    // For named instances the components should use getAxiosInstance(name); This concept makes possible to use multiple Rest endpoints on different backend server
    await createRestInstance({ store: store, baseURL: config.backend_url });

    socket.once("connect", function () {
        let session = localStorage.getItem(SESSION);

        if (session) {
            socket.emit(USER_CONNECTED, { Token: session });
        }

        socket.on(TOKEN_VALIDATION, handleTokenValidation.bind(socket));
        socket.on(TRANSLATIONS, message => store.dispatch(updateTranslations(message.message)));
    });

    // Set default locale - the mui picker components use this default locale, see the MuiPickersUtilProvider bellow
    const locale = "de";

    // Render React DOM
    ReactDOM.render(
        <>
            <Provider store={snackbarStore}>
                <CustomSnackbar />
            </Provider>
            <Provider store={store}>
                <Router history={History}>

                    <SnackbarProvider maxSnack={3}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        autoHideDuration={2000}
                        ref={snackbarRef}
                        action={(key) => (
                            <HighlightOff style={{ cursor: 'pointer' }} onClick={onClickDismiss(key)}>
                            </HighlightOff>
                        )} >
                        <SnackbarUtilsConfigurator />
                        <MuiPickersUtilsProvider utils={MomentUtils} locale={locale} moment={moment}>
                            <App socket={socket} />
                        </MuiPickersUtilsProvider>
                    </SnackbarProvider >
                </Router>
            </Provider>
        </>,
        document.getElementById("root")
    );

    // Register service worker
    // registerServiceWorker();
};

// Start the async index
index();
